import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LinkForm, type LinkDraftState } from './LinkForm.js';
import { QuestionForm, type QuestionDraftState } from './QuestionForm.js';
import { NoteForm, type NoteDraftState } from './NoteForm.js';
import { TipForm, type TipDraftState } from './TipForm.js';
import { AppForm, type AppDraftState } from './AppForm.js';
import { ProjectForm, type ProjectDraftState } from './ProjectForm.js';
import { appsDb, linksDb, notesDb, projectsDb, questionsDb, tipsDb } from '../../lib/db/index.js';
import { Spinner } from '../shared/Spinner.js';
import { toAppError } from '../../lib/utils/error.js';
import type { Visibility } from '../../types/visibility.js';
import type { Importance } from '../../types/link.js';
import type { AppStatus, UsageScope } from '../../types/app.js';
import type { ProjectStatus } from '../../types/project.js';

export type ComposeKind = 'link' | 'question' | 'note' | 'tip' | 'app' | 'project';

const KINDS: ReadonlySet<ComposeKind> = new Set([
  'link',
  'question',
  'note',
  'tip',
  'app',
  'project',
]);

const TITLES_CREATE: Record<ComposeKind, string> = {
  link: 'URL を共有',
  question: '質問する',
  note: '検証メモを書く',
  tip: 'TIPS を書く',
  app: '作成アプリを登録',
  project: 'トピックを作成',
};

const TITLES_EDIT: Record<ComposeKind, string> = {
  link: 'URL を編集',
  question: '質問を編集',
  note: '検証メモを編集',
  tip: 'TIPS を編集',
  app: '作成アプリを編集',
  project: 'トピックを編集',
};

/**
 * URL クエリパラメータ駆動のモーダル：
 *   ?compose=link で開く / ?compose=（空 or なし）で閉じる
 *   ?compose=link&edit=<id> で編集モード
 *   下書きは localStorage バックアップ（Form 側で管理、編集モード時はスキップ）
 */
export function ComposeModal() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const compose = params.get('compose');
  const editId = params.get('edit') ?? undefined;

  const kind = useMemo<ComposeKind | null>(() => {
    if (!compose || !KINDS.has(compose as ComposeKind)) return null;
    return compose as ComposeKind;
  }, [compose]);

  // 編集モード時のみ既存 doc を fetch
  const editQuery = useQuery({
    queryKey: ['compose-edit', kind, editId],
    queryFn: async () => {
      if (!kind || !editId) return null;
      switch (kind) {
        case 'link': {
          const doc = await linksDb.findById(editId);
          if (!doc) throw new Error('not-found');
          return { kind: 'link' as const, doc };
        }
        case 'question': {
          const doc = await questionsDb.findById(editId);
          if (!doc) throw new Error('not-found');
          return { kind: 'question' as const, doc };
        }
        case 'note': {
          const doc = await notesDb.findById(editId);
          if (!doc) throw new Error('not-found');
          return { kind: 'note' as const, doc };
        }
        case 'tip': {
          const doc = await tipsDb.findById(editId);
          if (!doc) throw new Error('not-found');
          return { kind: 'tip' as const, doc };
        }
        case 'app': {
          const doc = await appsDb.findById(editId);
          if (!doc) throw new Error('not-found');
          return { kind: 'app' as const, doc };
        }
        case 'project': {
          const doc = await projectsDb.findById(editId);
          if (!doc) throw new Error('not-found');
          return { kind: 'project' as const, doc };
        }
      }
    },
    enabled: !!kind && !!editId,
    staleTime: 0,
    gcTime: 0,
  });

  const close = () => {
    const next = new URLSearchParams(params);
    next.delete('compose');
    next.delete('edit');
    setParams(next, { replace: true });
  };

  useEffect(() => {
    if (!kind) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  if (!kind) return null;

  const isEditing = !!editId;
  const title = isEditing ? TITLES_EDIT[kind] : TITLES_CREATE[kind];

  // 編集モードで fetch 失敗時の表示
  const renderEditError = (msg: string) => (
    <div className="auth-error" style={{ margin: 16 }}>
      {msg}
    </div>
  );

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true">
      <div className="compose-modal">
        <div className="compose-head">
          <h2 className="modal-title">{title}</h2>
          <button type="button" className="btn btn-ghost" onClick={close}>
            閉じる
          </button>
        </div>
        <div className="compose-body">
          {isEditing && editQuery.isPending && <Spinner />}
          {isEditing && editQuery.isError &&
            renderEditError(toAppError(editQuery.error).userMessage)}
          {isEditing && editQuery.data === null &&
            renderEditError('対象が見つかりませんでした。')}

          {kind === 'link' && (!isEditing || editQuery.data?.kind === 'link') && (
            <LinkForm
              editId={editId}
              initialValues={
                editQuery.data?.kind === 'link'
                  ? linkDocToDraft(editQuery.data.doc)
                  : undefined
              }
              onDone={(id) => {
                close();
                navigate(`/links/${id}`);
              }}
            />
          )}
          {kind === 'question' && (!isEditing || editQuery.data?.kind === 'question') && (
            <QuestionForm
              editId={editId}
              initialValues={
                editQuery.data?.kind === 'question'
                  ? questionDocToDraft(editQuery.data.doc)
                  : undefined
              }
              onDone={(id) => {
                close();
                navigate(`/qa/${id}`);
              }}
            />
          )}
          {kind === 'note' && (!isEditing || editQuery.data?.kind === 'note') && (
            <NoteForm
              editId={editId}
              initialValues={
                editQuery.data?.kind === 'note'
                  ? noteDocToDraft(editQuery.data.doc)
                  : undefined
              }
              onDone={(id) => {
                close();
                navigate(`/notes/${id}`);
              }}
            />
          )}
          {kind === 'tip' && (!isEditing || editQuery.data?.kind === 'tip') && (
            <TipForm
              editId={editId}
              initialValues={
                editQuery.data?.kind === 'tip'
                  ? tipDocToDraft(editQuery.data.doc)
                  : undefined
              }
              onDone={(id) => {
                close();
                navigate(`/tips/${id}`);
              }}
            />
          )}
          {kind === 'app' && (!isEditing || editQuery.data?.kind === 'app') && (
            <AppForm
              editId={editId}
              initialValues={
                editQuery.data?.kind === 'app'
                  ? appDocToDraft(editQuery.data.doc)
                  : undefined
              }
              onDone={(id) => {
                close();
                navigate(`/apps/${id}`);
              }}
            />
          )}
          {kind === 'project' && (!isEditing || editQuery.data?.kind === 'project') && (
            <ProjectForm
              editId={editId}
              initialValues={
                editQuery.data?.kind === 'project'
                  ? projectDocToDraft(editQuery.data.doc)
                  : undefined
              }
              onDone={(id) => {
                close();
                navigate(`/projects/${id}`);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---- doc → draft マッパ（fetched doc を Form の DraftState 型に整形）----

function linkDocToDraft(d: {
  url: string;
  title: string;
  summary: string;
  userComment: string;
  importance: Importance;
  tags: string[];
  visibility: Visibility;
  thumbnailUrl?: string;
  channelName?: string;
  projectId?: string;
}): LinkDraftState {
  return {
    url: d.url,
    title: d.title,
    summary: d.summary,
    userComment: d.userComment,
    importance: d.importance,
    tags: d.tags,
    thumbnailUrl: d.thumbnailUrl ?? '',
    channelName: d.channelName ?? '',
    visibility: d.visibility,
    projectId: d.projectId,
  };
}

function questionDocToDraft(d: {
  title: string;
  body: string;
  tags: string[];
  visibility: Visibility;
  projectId?: string;
}): QuestionDraftState {
  return {
    title: d.title,
    body: d.body,
    tags: d.tags,
    visibility: d.visibility,
    projectId: d.projectId,
  };
}

function noteDocToDraft(d: {
  title: string;
  purpose: string;
  tried: string;
  result: string;
  conclusion: string;
  tags: string[];
  visibility: Visibility;
  projectId?: string;
}): NoteDraftState {
  return {
    title: d.title,
    purpose: d.purpose,
    tried: d.tried,
    result: d.result,
    conclusion: d.conclusion,
    tags: d.tags,
    visibility: d.visibility,
    projectId: d.projectId,
  };
}

function tipDocToDraft(d: {
  title: string;
  body: string;
  tags: string[];
  visibility: Visibility;
  projectId?: string;
}): TipDraftState {
  return {
    title: d.title,
    body: d.body,
    tags: d.tags,
    visibility: d.visibility,
    projectId: d.projectId,
  };
}

function appDocToDraft(d: {
  name: string;
  url: string;
  summary: string;
  purpose: string;
  technologies: string[];
  aiModel: string;
  usageScope: UsageScope;
  status: AppStatus;
  caution: string;
  tags: string[];
  visibility: Visibility;
  projectId?: string;
}): AppDraftState {
  return {
    name: d.name,
    url: d.url,
    summary: d.summary,
    purpose: d.purpose,
    technologies: d.technologies,
    aiModel: d.aiModel,
    usageScope: d.usageScope,
    status: d.status,
    caution: d.caution,
    tags: d.tags,
    visibility: d.visibility,
    projectId: d.projectId,
  };
}

function projectDocToDraft(d: {
  name: string;
  description: string;
  color: string;
  status: ProjectStatus;
  visibility: Visibility;
}): ProjectDraftState {
  return {
    name: d.name,
    description: d.description,
    color: d.color,
    status: d.status,
    visibility: d.visibility,
  };
}

